import taskManagerApi from '@/features/shared/services/taskManagerApi';
import { transformToRequestClient } from "@/features/shared/utils/helpers";
/**
 * Unified function to fetch clients.
 * This function can fetch:
 *  - Default clients (no pagination/filters),
 *  - All clients (if all=true), or
 *  - Clients in a specific page with filters.
 *
 * @param {number} [page] - The page number (if paginating).
 * @param {ClientFilters} [filters] - Filtering criteria.
 * @param {boolean} [all=false] - If true, fetch all clients.
 * @returns {Promise<ClientData>} - An object containing clients and metadata.
 */
export async function fetchClients(page, filters, all = false, limit) {
    try {
        const params = {
            ...(page ? { page } : {}),
            ...(filters || {}),
            ...(all ? { all: true } : {}),
            ...(limit ? { limit } : {}),
        };
        const response = await taskManagerApi.get('/client', { params });
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la récupération des clients");
        }
    }
}
/**
 * Fetch a single client by ID.
 */
export async function fetchClient(id) {
    try {
        const response = await taskManagerApi.get(`/client/${id}`);
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la récupération du client");
        }
    }
}
/**
 * Delete a client by its ID.
 */
export async function deleteClient(id) {
    try {
        await taskManagerApi.delete(`/client/${id}`);
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la suppression du client");
        }
    }
}
/**
 * Create a new client.
 */
export async function addClient(client) {
    try {
        const response = await taskManagerApi.post('/client', client);
        return response.data.data.client;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de l'ajout du client");
        }
    }
}
/**
 * Update an existing client.
 */
export async function updateClient(idClient, client) {
    try {
        const response = await taskManagerApi.put(`/client/${idClient}`, transformToRequestClient(client));
        return response.data.data;
    }
    catch (error) {
        if (error instanceof Error) {
            const serverError = error?.response?.data?.error;
            throw new Error(serverError?.details ?? serverError?.message ?? error.message);
        }
        else {
            throw new Error("Erreur inconnue lors de la mise à jour du client");
        }
    }
}
